import React, { useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from 'react-i18next';
import { APP_CONFIG } from '../config/constants';
import LanguageSwitcher from './LanguageSwitcher';
import { ReactComponent as Logo } from '../images/catalogarius.svg';

interface PageHeaderProps {
  additionalButtons?: React.ReactNode;
}

const PageHeader: React.FC<PageHeaderProps> = ({ 
  additionalButtons
}) => {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const history = useHistory();
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const handleLogout = () => {
    logout();
    history.push('/');
    setIsMobileMenuOpen(false);
  };

  const handleNavigation = (path: string) => {
    history.push(path);
    setIsMobileMenuOpen(false);
  };

  const getHomeRoute = () => {
    if (user?.role === 'admin') return '/dashboard';
    if (user?.role === 'client') return '/client-profile';
    if (user?.role === 'user') return '/image-upload';
    return '/';
  };

  const handleTitleClick = () => {
    history.push(getHomeRoute());
  };

  const getRoleBadge = () => {
    if (user?.role === 'admin') {
      return (
        <span className="ml-2 px-2 py-1 bg-purple-100 dark:bg-purple-900 text-purple-800 dark:text-purple-200 text-xs rounded-full">
          {t('common.admin')}
        </span>
      );
    }
    if (user?.role === 'client') {
      return (
        <span className="ml-2 px-2 py-1 bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 text-xs rounded-full">
          {t('common.client')}
        </span>
      );
    }
    if (user?.role === 'user') {
      return (
        <span className="ml-2 px-2 py-1 bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200 text-xs rounded-full">
          {t('common.user')}
        </span>
      );
    }
    return null;
  };

  const getDefaultButtons = () => {
    const buttons = [];

    if (user?.role === 'client' && location.pathname === '/client-profile') {
      buttons.push(
        <button 
          key="image-upload"
          onClick={() => handleNavigation('/image-upload')} 
          className="btn-secondary"
        >
          {t('navigation.imageUpload')}
        </button>
      );
    }

    if (user?.role === 'client' && location.pathname === '/image-upload') {
      buttons.push(
        <button 
          key="client-profile"
          onClick={() => handleNavigation('/client-profile')} 
          className="btn-secondary"
        >
          {t('navigation.clientProfile')}
        </button>
      );
    }

    buttons.push(
      <button 
        key="settings"
        onClick={() => handleNavigation('/settings')} 
        className="btn-secondary"
      >
        {t('navigation.settings')}
      </button>
    );

    buttons.push(
      <button 
        key="logout"
        onClick={handleLogout} 
        className="btn-primary"
      >
        {t('common.logout')}
      </button>
    );

    return buttons;
  };

  return (
    <nav className="bg-white dark:bg-gray-800 shadow-sm border-b border-gray-200 dark:border-gray-700">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          <div className="flex items-center">
            <Logo width={50} height={50} />
            <h1 
              className="text-2xl font-bold text-gray-900 dark:text-white cursor-pointer hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
              onClick={handleTitleClick}
            >
              {APP_CONFIG.appName}
            </h1>
          </div>
          
          {user && (
            <div className="hidden lg:flex items-center space-x-4">
              <span className="text-gray-700 dark:text-gray-300">
                {t('common.welcome')}, <span className="font-semibold">{user?.username}</span>
                {getRoleBadge()}
              </span>
              <LanguageSwitcher />
              {additionalButtons || getDefaultButtons()}
            </div>
          )}

          <div className="lg:hidden">
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="inline-flex items-center justify-center p-2 rounded-md text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500"
              aria-expanded={isMobileMenuOpen}
            >
              <span className="sr-only">Open main menu</span>
              {!isMobileMenuOpen ? (
                <svg className="block h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              ) : (
                <svg className="block h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              )}
            </button>
          </div>
        </div>
      </div>

      {isMobileMenuOpen && (
        <div className="lg:hidden">
          <div className="px-2 pt-2 pb-3 space-y-1 border-t border-gray-200 dark:border-gray-700">
            <div className="px-3 py-2 text-gray-700 dark:text-gray-300">
              {t('common.welcome')}, <span className="font-semibold">{user?.username}</span>
              {getRoleBadge()}
            </div>

            <div className="px-3 py-2">
              <LanguageSwitcher />
            </div>

            <div className="px-3 py-2 space-y-2">
              {additionalButtons ? (
                <div className="flex flex-col space-y-2">
                  {additionalButtons}
                </div>
              ) : (
                <div className="flex flex-col space-y-2">
                  {getDefaultButtons()}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </nav>
  );
};

export default PageHeader;
